import { FormEvent, useId, useMemo, useState } from 'react';
import { IModalDialogProps, ModalDialog } from '@ui/modal-dialog/dialog';
import { IAgriculture } from '@@/agriculture/ts';
import { onChangeByValuesFn, onChangeByValuesInterfaceKeysFn } from '@@/common/ts';
import { ISimpleMixingBag } from '@@/mixing/ts';
import { useImmer } from 'use-immer';
import { setByValues } from '@@/common/utils/set-by-values.util';
import { UiTextField } from '@@/ui/components/ui-text-field';
import { UiButton } from '@@/ui/components/ui-button';

import styles from './index.module.sass';

export function SimpleMixingCustomBagModal(props: ISimpleMixingCustomBagModalProps) {
  const { bag, agriculture, onAdd, onClose } = props;
  const [weight, setWeight] = useState<number>(bag?.weight ?? 1);

  const paramsFromBag = useMemo(
    () =>
      agriculture.parameters?.reduce(
        (acc, param) => ({
          ...acc,
          [param.name]:
            bag?.values?.find((value) => value.name === param.name)?.value ??
            param.options.min,
        }),
        {},
      ),
    [agriculture.parameters, bag?.values],
  );
  const [parameters, setParameters] =
    useImmer<Record<string, string | null>>(paramsFromBag);
  const onChangeParameters: onChangeByValuesFn = (values) => {
    setParameters((draft) => void setByValues(draft, values));
  };

  const formId = useId();
  const [isLoading, setIsLoading] = useState(false);
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    await onAdd({
      weight,
      agricultureId: agriculture.id,
      values: agriculture.parameters.map((parameter) => ({
        name: parameter.name,
        value: parameters[parameter.name] ?? null,
        units: parameter.units,
      })),
    });

    setIsLoading(false);
  };

  return (
    <ModalDialog
      title="Ввод параметров вручную"
      onClose={onClose}
      actions={
        <UiButton
          view="primary"
          label="Создать"
          type="submit"
          htmlForm={formId}
          loading={isLoading}
        />
      }
    >
      <form id={formId} className={styles.content} onSubmit={onSubmit}>
        {agriculture.parameters.map((parameter) => (
          <UiTextField
            key={parameter.id}
            label={parameter.name}
            type="number"
            value={parameters[parameter.name] ?? ''}
            required
            min={parameter.options?.min}
            max={parameter.options?.max}
            onChange={({ value }) => onChangeParameters({ [parameter.name]: value })}
          />
        ))}

        <UiTextField
          label="Вес"
          type="number"
          value={weight.toString()}
          min={1}
          required
          onChange={({ value }) => setWeight(Number(value))}
        />
      </form>
    </ModalDialog>
  );
}

export interface ISimpleMixingCustomBagModalProps {
  bag?: ISimpleMixingBag;
  agriculture: IAgriculture;

  onAdd: (bag: ISimpleMixingBag) => void | Promise<void>;
  onClose: IModalDialogProps['onClose'];
}
